'use client';

import { FC, useEffect, useState } from 'react';

import { Typography } from '../../../typography/components/Typography/Typography';

interface Props {
   steps: Array<string>;
   activeStep?: number;
   readOnly?: boolean;
   orientation?: 'horizontal' | 'vertical';
   onStepChange?: (step: number) => void;
}

export const LinearStepper: FC<Props> = ({ steps, activeStep = 0, readOnly = true, orientation = 'horizontal', onStepChange }) => {
   const [currentStep, setCurrentStep] = useState(activeStep);

   useEffect(() => setCurrentStep(activeStep), [activeStep]);

   const handleStepChange = (step: number) => {
      if (currentStep === step || readOnly) return;

      setCurrentStep(step);
      onStepChange?.(step);
   };

   return (
      <div
         data-testid={`linear-stepper--${orientation}`}
         className={`flex
              ${orientation === 'vertical' ? 'my-[calc(var(--component-navigation-linear-stepper-element-dot-container-sizing-size)*0.5)] flex-col h-full' : ''}
              ${orientation === 'horizontal' ? 'mx-[calc(var(--component-navigation-linear-stepper-container-spacing-padding-x-horizontal)+var(--component-navigation-linear-stepper-element-dot-container-sizing-size)*0.5)]' : ''}
           `.replace(/\s+/g, ' ')}
      >
         {steps.map((step, index) => (
            <div key={step} className={`flex ${index !== 0 ? 'flex-1' : ''} ${orientation === 'vertical' ? 'flex-col' : ''}`}>
               {index !== 0 && (
                  <div
                     className={`flex flex-1 items-center
                              ${orientation === 'vertical' ? 'flex flex-col items-center w-component-navigation-linear-stepper-element-line-container-sizing-width-vertical' : ''}
                              ${orientation === 'horizontal' ? 'flex items-center h-component-navigation-linear-stepper-element-line-container-sizing-height-horizontal' : ''}
                          `.replace(/\s+/g, ' ')}
                  >
                     <div
                        className={`
                                  ${orientation === 'vertical' ? 'h-full border-r-component-navigation-linear-stepper-line-border-width' : ''}
                                  ${orientation === 'horizontal' ? 'w-full border-b-component-navigation-linear-stepper-line-border-width' : ''}
                                  ${index <= currentStep ? 'border-component-navigation-linear-stepper-element-line-color-border-completed' : 'border-component-navigation-linear-stepper-element-line-color-border-enabled'}
                              `.replace(/\s+/g, ' ')}
                     />
                  </div>
               )}
               <div
                  data-testid={`linear-stepper-dot--${index}--${index === currentStep ? 'active' : 'inactive'}`}
                  onClick={() => handleStepChange(index)}
                  role="presentation"
                  className={`z-10 cursor-pointer
                      ${readOnly ? 'pointer-events-none' : ''}
                      ${orientation === 'vertical' ? 'flex flex-row items-center h-0 gap-component-navigation-linear-stepper-element-container-spacing-gap-horizontal' : ''}
                      ${orientation === 'horizontal' ? 'flex flex-col items-center w-0 gap-component-navigation-linear-stepper-element-container-spacing-gap-vertical' : ''}
                      `.replace(/\s+/g, ' ')}
               >
                  <div className="w-component-navigation-linear-stepper-element-dot-container-sizing-size h-component-navigation-linear-stepper-element-dot-container-sizing-size flex items-center justify-center">
                     <button
                        type="button"
                        className={`appearance-none rounded-full
                                 ${index > currentStep ? 'h-component-navigation-linear-stepper-element-dot-sizing-size-inactive w-component-navigation-linear-stepper-element-dot-sizing-size-inactive bg-component-navigation-linear-stepper-element-dot-color-fill-enabled' : ''}
                                 ${index === currentStep ? 'h-component-navigation-linear-stepper-element-dot-sizing-size-active w-component-navigation-linear-stepper-element-dot-sizing-size-active' : ''}
                                 ${index < currentStep ? 'h-component-navigation-linear-stepper-element-dot-sizing-size-completed w-component-navigation-linear-stepper-element-dot-sizing-size-completed' : ''}
                                 ${index <= currentStep ? 'bg-component-navigation-linear-stepper-element-dot-color-fill-completed' : ''}
                              `.replace(/\s+/g, ' ')}
                     />
                  </div>
                  <div
                     data-testid={`linear-stepper-label--${index}`}
                     onClick={() => handleStepChange(index)}
                     role="presentation"
                     className={`flex cursor-pointer justify-center
                              ${orientation === 'horizontal' ? 'w-component-navigation-linear-stepper-element-label-sizing-width-vertical' : ''}
                              ${index > currentStep ? 'text-component-navigation-linear-stepper-element-label-color-text-inactive' : ''}
                              ${index === currentStep ? 'text-component-navigation-linear-stepper-element-label-color-text-completed' : ''}
                              ${index < currentStep ? 'text-component-navigation-linear-stepper-element-label-color-text-active' : ''}

                          `.replace(/\s+/g, ' ')}
                  >
                     <Typography align="center" color="inherit" variant={index === currentStep ? 'small-highlight' : 'small'}>
                        {step}
                     </Typography>
                  </div>
               </div>
            </div>
         ))}
      </div>
   );
};
